import { AlertTriangle } from 'lucide-react';

interface Props {
  subscriptionEndsAt: string | null;
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function CancelSubscriptionModal({ subscriptionEndsAt, isPending, onConfirm, onCancel }: Props) {
  const endsDate = subscriptionEndsAt
    ? new Date(subscriptionEndsAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <div className="csm-overlay">
      <div className="csm-modal" onClick={e => e.stopPropagation()}>

        <div className="csm-icon-wrap">
          <AlertTriangle size={28} strokeWidth={2} className="csm-icon" />
        </div>

        <h3 className="csm-title">Cancel your subscription?</h3>

        <p className="csm-subtitle">
          {endsDate
            ? <>Your plan stays active until <strong>{endsDate}</strong>. After that:</>
            : <>After cancellation:</>}
        </p>

        <ul className="csm-list">
          <li>Lead and email credits will be revoked</li>
          <li>Active campaigns and sequences will be paused</li>
          <li>Your data is kept for 30 days, then permanently deleted</li>
        </ul>

        <div className="csm-actions">
          <button className="btn btn-secondary btn-sm csm-btn-keep" onClick={onCancel} disabled={isPending}>
            Keep Subscription
          </button>
          <button className="btn btn-sm csm-btn-cancel" onClick={onConfirm} disabled={isPending}>
            {isPending ? 'Cancelling…' : 'Yes, Cancel'}
          </button>
        </div>
      </div>

      <style>{`
        .csm-overlay {
          position: fixed; inset: 0;
          background: rgba(15, 23, 42, 0.55);
          backdrop-filter: blur(2px);
          z-index: 2000;
          display: flex; align-items: center; justify-content: center; padding: 16px;
        }
        .csm-modal {
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 24px 64px rgba(0,0,0,0.18);
          width: 100%; max-width: 420px;
          padding: 32px 28px 24px;
          text-align: center;
          animation: csm-in 0.18s ease;
        }
        @keyframes csm-in {
          from { opacity: 0; transform: translateY(10px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .csm-icon-wrap {
          width: 56px; height: 56px;
          border-radius: 50%;
          background: #FEF2F2;
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 18px;
        }
        .csm-icon { color: #E53935; }
        .csm-title {
          font-size: 18px; font-weight: 700;
          color: var(--navy, #1A2D5A);
          margin: 0 0 10px;
        }
        .csm-subtitle {
          font-size: 14px; color: var(--gray-500, #6B7280);
          margin: 0 0 16px; line-height: 1.5;
        }
        .csm-list {
          list-style: none; padding: 0; margin: 0 0 24px;
          background: #FFF7F7;
          border: 1px solid #FECACA;
          border-radius: 10px;
          text-align: left;
        }
        .csm-list li {
          font-size: 13px; color: #7F1D1D;
          padding: 9px 14px;
          display: flex; align-items: center; gap: 8px;
        }
        .csm-list li + li { border-top: 1px solid #FECACA; }
        .csm-list li::before {
          content: '✕';
          font-size: 11px; font-weight: 700;
          color: #E53935; flex-shrink: 0;
        }
        .csm-actions {
          display: flex; gap: 10px;
        }
        .csm-btn-keep { flex: 1; }
        .csm-btn-cancel {
          flex: 1;
          background: #E53935; color: #fff; border: none;
          border-radius: 8px;
        }
        .csm-btn-cancel:hover:not(:disabled) { background: #C62828; }
        .csm-btn-cancel:disabled { opacity: 0.6; cursor: not-allowed; }
      `}</style>
    </div>
  );
}
